import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarCheck, ShieldCheck, Activity } from "lucide-react";

const FEATURES = [
  { icon: CalendarCheck, text: "Plan monthly check-up batches in seconds" },
  { icon: Activity, text: "Track attendance and carry over no-shows automatically" },
  { icon: ShieldCheck, text: "Master & staff roles keep your records safe" },
];

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand hero */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-950 text-white p-12">
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-teal-500/30 blur-3xl animate-float-glow" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl animate-float-glow" style={{ animationDelay: "2.5s" }} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative flex items-center gap-3"
        >
          <div className="h-11 w-11 rounded-xl p-[2px] bg-gradient-to-br from-teal-300 to-emerald-500 shadow-lg shadow-teal-500/30">
            <div className="h-full w-full rounded-[10px] overflow-hidden bg-slate-950">
              <img src="/tooth-logo.png" alt="OrthoBatch logo" className="h-full w-full object-cover" />
            </div>
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">OrthoBatch</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative space-y-7 max-w-md"
        >
          <div className="space-y-4">
            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium tracking-wide text-teal-200">
              Orthodontic Appointment Manager
            </span>
            <h1 className="font-heading text-4xl font-bold tracking-tight leading-[1.1]">
              Keep every treatment <span className="text-teal-300">on schedule.</span>
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Plan monthly check-up batches, verify attendance, and never lose sight of a
              patient who keeps missing appointments.
            </p>
          </div>
          <ul className="space-y-3">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.25 + i * 0.1 }}
                className="flex items-center gap-3 text-sm text-slate-200"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-500/15 text-teal-300 ring-1 ring-teal-400/20">
                  <Icon className="h-4 w-4" />
                </span>
                {text}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <p className="relative text-xs text-slate-500">Clinic scheduling, organised.</p>
      </div>

      {/* Sign-in form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl p-[2px] bg-gradient-to-br from-teal-300 to-emerald-500">
              <div className="h-full w-full rounded-[9px] overflow-hidden">
                <img src="/tooth-logo.png" alt="OrthoBatch logo" className="h-full w-full object-cover" />
              </div>
            </div>
            <span className="font-heading font-bold">OrthoBatch</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-7">
            Sign in with your master or client staff account.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-username-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clinic.com"
                className="h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-lg h-11 shadow-sm transition-all active:scale-[0.99]"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-teal-700 hover:text-teal-800 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
